import { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ExternalLink, Navigation } from "lucide-react";
import { api } from "@/lib/api";
import { usePoll } from "@/lib/live";
import { fmtDateTime } from "@/lib/format";
import { EmptyState } from "@/components/common";

const STALE_MS = 5 * 60 * 1000;
const isLive = (r) => r.online !== false && Date.now() - new Date(r.updated_at).getTime() < STALE_MS;
const icon = (live, name, avatar) => L.divIcon({
  className: "",
  html: `<div style="display:flex;flex-direction:column;align-items:center;transform:translateY(-8px)"><div style="background:${live ? "#10b981" : "#94a3b8"};color:#fff;font:600 11px sans-serif;padding:2px 8px;border-radius:999px;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.25)">${name}</div>${avatar
    ? `<img src="${avatar}" alt="" style="width:38px;height:38px;border-radius:50%;object-fit:cover;border:3px solid ${live ? "#10b981" : "#94a3b8"};margin-top:3px;box-shadow:0 2px 6px rgba(0,0,0,.3);background:#fff"/>`
    : `<div style="width:14px;height:14px;border-radius:50%;background:${live ? "#10b981" : "#94a3b8"};border:3px solid #fff;margin-top:3px;box-shadow:0 0 0 2px ${live ? "#10b981" : "#94a3b8"}"></div>`}</div>`,
  iconSize: [0, 0],
});

const TrackMap = ({ rows, focus }) => {
  const el = useRef(null);
  const map = useRef(null);
  const layer = useRef(null);
  const fitted = useRef(false);
  useEffect(() => {
    map.current = L.map(el.current).setView([-6.2, 106.816], 11);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap" }).addTo(map.current);
    layer.current = L.layerGroup().addTo(map.current);
    return () => { map.current.remove(); fitted.current = false; };
  }, []);
  useEffect(() => {
    layer.current.clearLayers();
    rows.forEach((r) => L.marker([r.lat, r.lng], { icon: icon(isLive(r), r.name, r.avatar) }).addTo(layer.current));
    if (rows.length && !fitted.current) { map.current.fitBounds(rows.map((r) => [r.lat, r.lng]), { padding: [40, 40], maxZoom: 16 }); fitted.current = true; }
  }, [rows]);
  useEffect(() => { if (focus) map.current.setView([focus.lat, focus.lng], 17); }, [focus]);
  return <div ref={el} className="w-full h-[55vh] min-h-[320px] rounded-2xl overflow-hidden border z-0" data-testid="tracking-map" />;
};

export default function LiveTrackingPage() {
  const [rows, setRows] = useState(null);
  const [focus, setFocus] = useState(null);
  const load = useCallback(() => { api.get("/tracking").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load]);
  usePoll(load);
  return (
    <div>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-5">Live Tracking</h1>
      {rows === null ? <p className="text-muted-foreground">Memuat...</p> : rows.length === 0 ? (
        <EmptyState icon={Navigation} title="Belum ada posisi" text="Posisi muncul saat petugas lapangan membuka aplikasi dengan GPS aktif." testId="tracking-empty" />
      ) : (
        <div className="grid lg:grid-cols-[1fr_340px] gap-5">
          <TrackMap rows={rows} focus={focus} />
          <div className="space-y-2" data-testid="tracking-list">
            {rows.map((r) => (
              <div key={r.user_id} className="rounded-2xl border bg-card p-3.5" data-testid={`tracking-row-${r.user_id}`}>
                <button type="button" className="w-full text-left flex items-center gap-3" onClick={() => setFocus({ lat: r.lat, lng: r.lng, t: Date.now() })} data-testid={`tracking-focus-${r.user_id}`}>
                  <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${isLive(r) ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
                  <span className="min-w-0 flex-1"><span className="block font-semibold truncate">{r.name}</span><span className="block text-xs text-muted-foreground truncate">{r.resort_name || "Tanpa resort"} · {isLive(r) ? "Online" : "Terakhir"} {fmtDateTime(r.updated_at)}</span></span>
                </button>
                <div className="flex items-center justify-between mt-2">
                  <span className="font-mono text-[11px] text-muted-foreground">{r.lat.toFixed(5)}, {r.lng.toFixed(5)}</span>
                  <a href={`https://www.google.com/maps?q=${r.lat},${r.lng}`} target="_blank" rel="noreferrer" className="text-xs font-medium text-primary inline-flex items-center gap-1" data-testid={`tracking-gmaps-${r.user_id}`}>Google Maps<ExternalLink className="h-3 w-3" /></a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
